import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { inventoryApi } from '../../api';
import {
  PageHeader,
  LoadingState,
  ErrorState,
  EmptyState,
} from '../../components/common';
import { fmtCurrency, fmtMoney } from '../../utils/currency';

/**
 * Reorder suggestions — FR-INV-02.20
 * Read-only list of products at/below reorder point, with primary supplier +
 * last purchase cost to help turn into a PO.
 */
export function ReorderSuggestionsPage() {
  const [warehouseId, setWarehouseId] = useState('');

  const whQuery = useQuery({ queryKey: ['warehouses'], queryFn: inventoryApi.listWarehouses });
  const suggestQuery = useQuery({
    queryKey: ['inv', 'reorder', warehouseId],
    queryFn: () => inventoryApi.getReorderSuggestions(warehouseId || undefined),
  });

  const kpis = useMemo(() => {
    const rows = suggestQuery.data ?? [];
    let stockedOut = 0, totalSuggested = 0, estimatedCost = 0;
    for (const s of rows) {
      if (s.availableQty <= 0) stockedOut++;
      if (s.suggestedQty !== null) totalSuggested += s.suggestedQty;
      if (s.suggestedQty !== null && s.lastPurchaseCost !== null) {
        estimatedCost += s.suggestedQty * s.lastPurchaseCost;
      }
    }
    return { total: rows.length, stockedOut, totalSuggested, estimatedCost };
  }, [suggestQuery.data]);

  return (
    <div className="px-8 py-10 max-w-7xl mx-auto">
      <PageHeader
        eyebrow="Inventory"
        title="Reorder suggestions"
        subtitle="Products at or below reorder point. Set thresholds on product detail pages to appear here."
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiTile label="Items to reorder" value={kpis.total} />
        <KpiTile label="Stocked out" value={kpis.stockedOut}
          accent={kpis.stockedOut > 0 ? 'danger' : 'default'} />
        <KpiTile label="Total suggested units" value={kpis.totalSuggested.toLocaleString()} />
        <KpiTile label="Estimated PO value" value={fmtMoney(kpis.estimatedCost)} accent="sage" />
      </div>

      {/* Warehouse filter */}
      <div className="card p-4 mb-4 flex gap-3 items-end flex-wrap">
        <div className="flex-1 max-w-xs">
          <label className="label">Warehouse</label>
          <select className="input" value={warehouseId}
            onChange={(e) => setWarehouseId(e.target.value)}>
            <option value="">All warehouses</option>
            {whQuery.data?.map((w: any) => (
              <option key={w.id} value={w.id}>{w.code} — {w.name}</option>
            ))}
          </select>
        </div>
        <div className="text-xs text-ink-500 max-w-sm">
          Products without a reorder threshold won't appear. Set thresholds on the product detail page.
        </div>
      </div>

      {suggestQuery.isLoading ? (
        <div className="card"><LoadingState rows={5} /></div>
      ) : suggestQuery.error ? (
        <ErrorState error={suggestQuery.error} />
      ) : !suggestQuery.data?.length ? (
        <div className="card">
          <EmptyState
            message="Nothing to reorder"
            hint="All products with thresholds are stocked above their reorder point. Nice!"
          />
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th className="text-right">Available</th>
                  <th className="text-right">Reorder pt.</th>
                  <th className="text-right">Suggested</th>
                  <th>Supplier</th>
                  <th className="text-right">Last cost</th>
                </tr>
              </thead>
              <tbody>
                {(suggestQuery.data ?? []).map((s: any) => (
                  <tr key={s.productId}>
                    <td>
                      <div className="font-medium text-ink-900">{s.name}</div>
                      <div className="font-mono text-2xs text-ink-500 mt-0.5">{s.sku}</div>
                    </td>
                    <td className="text-right">
                      <span className={`font-mono font-semibold tabular-nums ${
                        s.availableQty <= 0 ? 'text-danger' : 'text-warning'
                      }`}>
                        {s.availableQty.toFixed(0)}
                      </span>
                      {s.availableQty <= 0 && (
                        <div className="text-2xs text-danger font-medium">out</div>
                      )}
                    </td>
                    <td className="text-right font-mono text-sm text-ink-500 tabular-nums">
                      {s.reorderPoint.toFixed(0)}
                    </td>
                    <td className="text-right">
                      {s.suggestedQty !== null ? (
                        <span className="font-mono font-semibold text-ink-900 tabular-nums">
                          {s.suggestedQty.toFixed(0)}
                        </span>
                      ) : (
                        <span className="text-2xs text-ink-400 italic">set qty</span>
                      )}
                    </td>
                    <td className="text-sm text-ink-700">
                      {s.supplierName ?? <span className="text-ink-400">—</span>}
                    </td>
                    <td className="text-right font-mono text-sm text-ink-700 tabular-nums">
                      {s.lastPurchaseCost !== null ? `${fmtCurrency(s.lastPurchaseCost)}` : <span className="text-ink-400">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


function KpiTile({
  label, value, accent = 'default',
}: {
  label: string;
  value: number | string;
  accent?: 'default' | 'sage' | 'amber' | 'danger';
}) {
  const bar = {
    default: 'bg-ink-200',
    sage: 'bg-sage-500',
    amber: 'bg-amber-500',
    danger: 'bg-danger',
  }[accent];
  return (
    <div className="card p-5 relative overflow-hidden">
      <div className={`absolute top-0 left-0 right-0 h-0.5 ${bar}`} />
      <div className="eyebrow text-ink-500">{label}</div>
      <div className="mt-3 text-3xl font-bold font-display text-ink-900 tabular-nums">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  );
}
